import { useCallback, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter } from 'react-router-dom';
import { TranslationsProvider } from '@izettle/lingo-client';
import { UserContext } from '@izettle/react';
import { captureException } from '@sentry/react';
import { ErrorPage } from '../components/error-page/index.js';
import { PageTemplate } from '../components/page-template.js';
import { RedirectsHandler } from '../components/redirects/handler.js';
import { DebuggerLoader } from '../components/z-debugger/loader.js';
import { useSentrySetup } from '../hooks/use-sentry-setup.js';
import { AuthProvider } from '../providers/auth.js';
import { ConfigContext } from '../providers/config.js';
import { HideLayoutProvider } from '../providers/hide-layout.js';
import { OverridesProvider } from '../providers/overrides.js';
import { ServiceProvider, UrlType } from '../providers/services.js';
import { UserProvider } from '../providers/user.js';
import { GlobalStyle } from '../styles.js';
import { pushGTMVisitorInfoDataLayer, setHtmlLang, removeLoader } from '../utils/rest.js';
import { jsx, jsxs } from 'react/jsx-runtime';

const handleError = error => {
  captureException(error);
  removeLoader();
};
const CustomErrorBoundary = _ref => {
  let {
    locale,
    children: errorBoundaryChildren
  } = _ref;
  const fallback = useMemo(() => /*#__PURE__*/jsx(ErrorPage, {
    locale: locale
  }), [locale]);
  return /*#__PURE__*/jsx(ErrorBoundary, {
    fallback: fallback,
    onError: handleError,
    children: errorBoundaryChildren
  });
};
const BackOfficeSetup = _ref2 => {
  let {
    config,
    disableKycRedirect,
    sentryConfig,
    hideLayout,
    allowStaff,
    urls,
    footerLinks,
    debuggerModules = [],
    pageLoaderTimeout = 1000,
    translations = {},
    onReceiveUser,
    children
  } = _ref2;
  const {
    setSentryData
  } = useSentrySetup(sentryConfig);
  const internalOnReceiveUser = useCallback(user => {
    setSentryData({
      user,
      config
    });
    pushGTMVisitorInfoDataLayer(user);
    setHtmlLang(user);
    onReceiveUser?.(user);
  }, [onReceiveUser]);

  // We have multiple error boundaries to account for certain error cases
  return (
    /*#__PURE__*/
    // No user data - need to fall back to browser language
    jsx(CustomErrorBoundary, {
      children: /*#__PURE__*/jsx(BrowserRouter, {
        children: /*#__PURE__*/jsx(ConfigContext.Provider, {
          value: {
            config
          },
          children: /*#__PURE__*/jsx(OverridesProvider, {
            children: /*#__PURE__*/jsx(ServiceProvider, {
              type: UrlType.Base,
              children: /*#__PURE__*/jsx(AuthProvider, {
                children: /*#__PURE__*/jsx(UserProvider, {
                  onReceiveUser: internalOnReceiveUser,
                  children: /*#__PURE__*/jsx(ServiceProvider, {
                    type: UrlType.Service,
                    children: /*#__PURE__*/jsx(RedirectsHandler, {
                      allowStaff: allowStaff ?? false,
                      disableKycRedirect: disableKycRedirect ?? false,
                      children: /*#__PURE__*/jsx(UserContext.Consumer, {
                        children: userData => /*#__PURE__*/jsx(HideLayoutProvider, {
                          hideLayout: userData.isMobileApp || hideLayout,
                          children: /*#__PURE__*/jsxs(TranslationsProvider, {
                            ...translations,
                            locale: userData.locale,
                            children: [/*#__PURE__*/jsx(GlobalStyle, {}), process.env.NODE_ENV !== "test" && process.env.APP_ENV !== "production" && /*#__PURE__*/jsx(DebuggerLoader, {
                              modules: debuggerModules
                            }), /*#__PURE__*/jsx(PageTemplate, {
                              urls: urls,
                              footerLinks: footerLinks,
                              onLoaded: () => removeLoader(pageLoaderTimeout),
                              children: /*#__PURE__*/jsx(CustomErrorBoundary, {
                                locale: userData.locale.isoLocale,
                                children: children
                              })
                            })]
                          })
                        })
                      })
                    })
                  })
                })
              })
            })
          })
        })
      })
    })
  );
};

export { BackOfficeSetup };
//# sourceMappingURL=back-office-setup.js.map
